import {
  Button,
  Dialog,
  Flex,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCreateAskPunttProject } from "../../../utils/queries/askPunttProjects";

export type CreatedAskPunttProject = {
  id: string;
  name: string;
  sharedChatId: string | null;
};

/**
 * Home composer modal to name a new Ask Puntt project (and optional
 * description). On success the shared chat is still created server-side; the
 * caller selects the project in the home picker and stays on home.
 */
export function CreateAskPunttProjectDialog({
  open,
  onOpenChange,
  onCreated,
  createFn,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (project: CreatedAskPunttProject) => void;
  /** V2 passes the warhol projects endpoint; default is christo. */
  createFn?: Parameters<typeof useCreateAskPunttProject>[0];
}) {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const createProject = useCreateAskPunttProject(createFn);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) {
      setName("");
      setDescription("");
    }
  }, [open]);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !createProject.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const result = await createProject.mutateAsync({
        name: trimmedName,
        description: description.trim(),
      });
      onCreated({
        id: result.project._id,
        name: result.project.name,
        sharedChatId:
          result.sharedChatId ?? result.project.sharedChatId ?? null,
      });
      onOpenChange(false);
    } catch {
      // useCreateAskPunttProject shows an error toast; keep the dialog open.
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="440px"
        data-testid="ask-puntt-create-project-dialog"
        aria-labelledby="ask-puntt-create-project-title"
      >
        <Dialog.Title id="ask-puntt-create-project-title">
          {tCommon("nav.newProject")}
        </Dialog.Title>
        <Dialog.Description size="2" mb="3">
          {t("home.createProjectDescription")}
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <label htmlFor="ask-puntt-create-project-name">
            <Text size="2" weight="medium" className="mb-1 block">
              {t("projectName")}
            </Text>
            <TextField.Root
              id="ask-puntt-create-project-name"
              data-testid="ask-puntt-create-project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("projectName")}
              autoFocus
            />
          </label>

          <label htmlFor="ask-puntt-create-project-description">
            <Text size="2" weight="medium" className="mb-1 block">
              {t("home.createProjectDescriptionLabel")}
            </Text>
            <TextArea
              id="ask-puntt-create-project-description"
              data-testid="ask-puntt-create-project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("home.createProjectDescriptionPlaceholder")}
              rows={3}
              resize="vertical"
            />
          </label>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button
              type="button"
              variant="soft"
              color="gray"
              data-testid="ask-puntt-create-project-cancel"
            >
              {tCommon("cancel")}
            </Button>
          </Dialog.Close>
          <Button
            type="button"
            data-testid="ask-puntt-create-project-submit"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {t("home.createProjectSubmit")}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
